import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, MapPin, Calendar, Briefcase, Code2 } from 'lucide-react';
import { PageTransition } from '../transitions/PageTransition';
import { useCursorContext } from '../../hooks/useCursorContext';
import { nikoInfo, skillCategories, industries, stats } from '../../data/portfolioData';
import { AnimatedCounter } from '../ui/AnimatedCounter';
import { SEOHead } from '../seo/SEOHead';

gsap.registerPlugin(ScrollTrigger);

export function AboutPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const bioRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const { setCursorState } = useCursorContext();

  useEffect(() => {
    let ctx: gsap.Context;

    const timer = setTimeout(() => {
      ctx = gsap.context(() => {
        // Hero animation - immediate, no ScrollTrigger (above fold)
        const heroElements = heroRef.current?.querySelectorAll('.hero-animate');
        if (heroElements && heroElements.length > 0) {
          gsap.fromTo(
            heroElements,
            { y: 60, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 1,
              stagger: 0.15,
              ease: 'power3.out',
            }
          );
        }

        // Bio text reveal - immediate for visible content
        const bioLines = bioRef.current?.querySelectorAll('.bio-line');
        if (bioLines) {
          gsap.fromTo(
            bioLines,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.1,
              ease: 'power2.out',
              delay: 0.3,
            }
          );
        }

        // Stats counter animation
        const statItems = statsRef.current?.querySelectorAll('.stat-item');
        if (statItems) {
          gsap.fromTo(
            statItems,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              stagger: 0.1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: statsRef.current,
                start: 'top 85%',
              },
            }
          );
        }

        // Skills categories animation
        const skillCards = skillsRef.current?.querySelectorAll('.skill-card');
        if (skillCards) {
          gsap.fromTo(
            skillCards,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              stagger: 0.1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: skillsRef.current,
                start: 'top 85%',
              },
            }
          );
        }

        // Industries animation
        const industryItems = industriesRef.current?.querySelectorAll('.industry-item');
        if (industryItems) {
          gsap.fromTo(
            industryItems,
            { x: -20, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.5,
              stagger: 0.05,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: industriesRef.current,
                start: 'top 85%',
              },
            }
          );
        }

        ScrollTrigger.refresh();
      }, containerRef);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (ctx) ctx.revert();
    };
  }, []);

  return (
    <PageTransition>
      <SEOHead
        title="Über mich"
        description="Niko Knez - Senior Fullstack Developer & UX/UI Designer mit 12+ Jahren Erfahrung. Skills, Technologien und Branchen-Erfahrung."
        path="/about"
      />
      <div ref={containerRef} className="min-h-screen bg-[#0a0a0a]">
        {/* Hero Section */}
        <section ref={heroRef} className="pt-32 md:pt-40 pb-20 px-8 md:px-12">
          <div className="max-w-6xl mx-auto">
            {/* Section label */}
            <span className="hero-animate text-xs text-white/30 tracking-[0.3em] uppercase block mb-8">
              Über mich
            </span>

            {/* Main heading */}
            <h1 className="hero-animate text-4xl md:text-6xl lg:text-7xl font-light text-white leading-tight mb-8">
              {nikoInfo.name}
              <br />
              <span className="text-emerald-500">{nikoInfo.title.split('&')[0]?.trim()}</span>
              <br />
              <span className="text-white/50">&amp; {nikoInfo.title.split('&')[1]?.trim() ?? 'Designer'}</span>
            </h1>

            {/* Quick info */}
            <div className="hero-animate flex flex-wrap gap-6 text-sm text-white/40">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {nikoInfo.location}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {nikoInfo.experience} Jahre Erfahrung
              </span>
              <span className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                50+ Projekte
              </span>
            </div>
          </div>
        </section>

        {/* Bio Section */}
        <section ref={bioRef} className="py-20 px-8 md:px-12 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16">
              {/* Left: Bio text */}
              <div className="space-y-6">
                <p className="bio-line text-2xl md:text-3xl font-light text-white/90 leading-relaxed">
                  {nikoInfo.shortBio}
                </p>
                <p className="bio-line text-lg text-white/50 leading-relaxed">
                  {nikoInfo.fullBio}
                </p>
                <p className="bio-line text-lg text-white/40 leading-relaxed">
                  Aktuell bei Systempiloten entwickle ich ein KI-gestütztes CMS/CRM-System mit
                  integriertem Webbuilder. Davor habe ich digitale Produkte für Mercedes-Benz,
                  E.ON und Procter & Gamble mitgestaltet.
                </p>
              </div>

              {/* Right: Philosophy */}
              <div className="space-y-8">
                <div className="bio-line">
                  <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-4">
                    Philosophie
                  </h3>
                  <p className="text-white/60 leading-relaxed">
                    Gute digitale Produkte entstehen durch die perfekte Balance aus
                    nutzerorientiertem Design, solider Technik und klarer Kommunikation.
                    Ich glaube an Code, der wartbar ist, und Design, das funktioniert.
                  </p>
                </div>
                <div className="bio-line">
                  <h3 className="text-xs text-white/30 tracking-[0.3em] uppercase mb-4">
                    Arbeitsweise
                  </h3>
                  <p className="text-white/60 leading-relaxed">
                    Ob als Lead Developer im agilen Team oder als Solo-Freelancer –
                    ich liefere qualitativ hochwertige Ergebnisse. Remote oder vor Ort,
                    mit klarer Kommunikation und Fokus auf das Wesentliche.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section ref={statsRef} className="py-16 px-8 md:px-12 bg-white/[0.02]">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="stat-item text-center p-6 border border-white/5 rounded-xl"
                >
                  <AnimatedCounter
                    value={stat.value}
                    className="text-3xl md:text-4xl font-light text-emerald-500"
                  />
                  <span className="block text-sm text-white/40 mt-2">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section ref={skillsRef} className="py-20 px-8 md:px-12">
          <div className="max-w-6xl mx-auto">
            <span className="text-xs text-white/30 tracking-[0.3em] uppercase block mb-12">
              Skills & Technologien
            </span>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {skillCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <div
                    key={category.title}
                    className="skill-card p-6 border border-white/5 rounded-xl hover:border-emerald-500/30 transition-colors group"
                    onMouseEnter={() => setCursorState('hovering')}
                    onMouseLeave={() => setCursorState('default')}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-emerald-500/10 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
                        <Icon className="w-5 h-5 text-emerald-500" />
                      </div>
                      <h3 className="text-white font-medium">{category.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.skills.map((skill) => (
                        <li key={skill.name} className="flex items-center justify-between">
                          <span className="text-sm text-white/60">{skill.name}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              skill.level === 'expert'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : skill.level === 'advanced'
                                  ? 'bg-white/10 text-white/50'
                                  : 'bg-white/5 text-white/30'
                            }`}
                          >
                            {skill.level === 'expert'
                              ? 'Expert'
                              : skill.level === 'advanced'
                                ? 'Fortgeschritten'
                                : 'Solide'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section ref={industriesRef} className="py-20 px-8 md:px-12 bg-white/[0.02]">
          <div className="max-w-6xl mx-auto">
            <span className="text-xs text-white/30 tracking-[0.3em] uppercase block mb-12">
              Branchen-Erfahrung
            </span>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {industries.map((industry) => (
                <div
                  key={industry.id}
                  className="industry-item p-4 border border-white/5 rounded-lg hover:border-emerald-500/30 transition-colors group"
                  onMouseEnter={() => setCursorState('hovering')}
                  onMouseLeave={() => setCursorState('default')}
                >
                  <h4 className="text-white/80 group-hover:text-emerald-500 transition-colors font-medium">
                    {industry.name}
                  </h4>
                  <p className="text-xs text-white/30 mt-1">{industry.project}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-8 md:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 p-8 md:p-12 border border-white/10 rounded-2xl bg-gradient-to-br from-emerald-500/5 to-transparent">
              <div>
                <h3 className="text-2xl md:text-3xl font-light text-white mb-2">
                  Bereit für dein Projekt?
                </h3>
                <p className="text-white/50">
                  Lass uns über deine Ideen sprechen und gemeinsam etwas Großartiges schaffen.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-6 py-3 bg-emerald-500 text-black rounded-full font-medium hover:bg-emerald-400 transition-colors group"
                onMouseEnter={() => setCursorState('hovering')}
                onMouseLeave={() => setCursorState('default')}
              >
                <span>Kontakt aufnehmen</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* Experience Teaser */}
        <section className="py-20 px-8 md:px-12 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <span className="text-xs text-white/30 tracking-[0.3em] uppercase">
                Vollständiger Werdegang
              </span>
              <Link
                to="/experience"
                className="text-sm text-emerald-500 hover:text-emerald-400 transition-colors flex items-center gap-2"
                onMouseEnter={() => setCursorState('hovering')}
                onMouseLeave={() => setCursorState('default')}
              >
                Alle Stationen
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="flex items-center gap-4 p-4 border border-white/5 rounded-lg">
              <Code2 className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-white/80">
                  Von der ersten Zeile Code 2015 bis heute – mein kompletter Werdegang
                  mit 12+ Stationen bei DAX-Konzernen, Agenturen und als Freelancer.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-8 md:px-12 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-xs text-white/20">
              © {new Date().getFullYear()} NK Design
            </span>
            <Link
              to="/impressum"
              className="text-xs text-white/20 hover:text-emerald-500 transition-colors"
              onMouseEnter={() => setCursorState('hovering')}
              onMouseLeave={() => setCursorState('default')}
            >
              Impressum
            </Link>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
}
